import React, { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18n';
import { WaypointTypeEnum, TmsRouteTypeEnum } from '../../../../../enums/TmsEnum';
import { useStepDetailRender, type OptionalString, formatTimeDisplay } from './hooks/useStepDetailRender';
import { useTaskCommon } from '../../hooks/useTaskCommon';
import EditToolbar, { useEditToolbar } from '../EditToolbar';
import { useWarehousePermissionGuardedStartEdit } from '../../hooks/useWarehousePermissionGuardedStartEdit';
import { GET_TMS_TASK_DETAIL } from '../../graphql/queries/getTmsTaskDetail';
import type { TmsTaskWaypointType, TmsTaskDetailType } from '../../graphql/queries/getTmsTaskDetail';
import { UpdateTmsTaskWaypointToArrivedInput, UPDATE_TMS_TASK_WAYPOINT_TO_ARRIVED_FOR_PORTAL } from '../../graphql/mutations/taskMutations';
import { getUserInfo } from '../../../../../utils/auth'
import { translateBackendError } from './utils/errorMessage';

interface DriverArrivedDetailProps {
    waypoint: TmsTaskWaypointType;
    stepDetailAllowEdit: boolean;
    task?: TmsTaskDetailType;
}

const DriverArrivedDetail: React.FC<DriverArrivedDetailProps> = ({ waypoint, stepDetailAllowEdit, task }) => {
    const [plannedArrivalTime, setPlannedArrivalTime] = useState<OptionalString>(formatTimeDisplay(waypoint.planArrivalAt));
    const [plannedArrivalTimezone] = useState<OptionalString>(waypoint.planArrivalTimezone);
    const [actualArrivalTime, setActualArrivalTime] = useState<OptionalString>(formatTimeDisplay(waypoint.actualArrivalAt));
    const [actualArrivalTimezone] = useState<OptionalString>(waypoint.actualArrivalTimezone);
    const [arrivalPhotoUrls, setArrivalPhotoUrls] = useState<string[]>(waypoint.arrivalPhotosUrlsJson || []);
    const [arrivalSealPhotoUrls, setArrivalSealPhotoUrls] = useState<string[]>(waypoint.arrivalSealPhotosUrlsJson || []);

    const [actualDraft, setActualDraft] = useState<Dayjs | null>(waypoint.actualArrivalAt ? dayjs(waypoint.actualArrivalAt) : null);
    const [photosDraft, setPhotosDraft] = useState<string[]>(waypoint.arrivalPhotosJson || []);
    const [sealPhotosDraft, setSealPhotosDraft] = useState<string[]>(waypoint.arrivalSealPhotosJson || []);
    const [remarkDraft, setRemarkDraft] = useState<string>(waypoint.arrivedRemark || '');

    const showSealPhotos = waypoint.waypointType === WaypointTypeEnum.Waypoint || waypoint.waypointType === WaypointTypeEnum.Destination;

    const { isEditing, startEdit, handleCancel, exitEdit } = useEditToolbar({
        onStartEdit: useCallback(() => {
            setActualDraft(waypoint.actualArrivalAt ? dayjs(waypoint.actualArrivalAt) : null);
            setPhotosDraft(waypoint.arrivalPhotosJson || []);
            setSealPhotosDraft(waypoint.arrivalSealPhotosJson || []);
            setRemarkDraft(waypoint.arrivedRemark || '');
        }, [waypoint.actualArrivalAt, waypoint.arrivalPhotosJson, waypoint.arrivalSealPhotosJson, waypoint.arrivedRemark]),
    });
    const guardedStartEdit = useWarehousePermissionGuardedStartEdit(waypoint.waypointId, startEdit);

    /**
     * 更新到达状态
     */
    const [updateArrivedMutation] = useMutation<{ updateTmsTaskWaypointToArrivedForPortal: boolean }, { input: UpdateTmsTaskWaypointToArrivedInput }>(
        UPDATE_TMS_TASK_WAYPOINT_TO_ARRIVED_FOR_PORTAL
    );

    /**
     * 国际化标签属性
     */
    const plannedArrivalTimeLabel = t('tms.task.drawer.plannedArrivalTime');
    const staticEtaLabel = t('tms.task.drawer.staticEta');
    const actualArrivalTimeLabel = t('tms.task.drawer.actualArrivalTime');
    const photoLabel = t('tms.task.drawer.vehicleArrivalPhotos');
    const sealPhotoLabel = t('tms.task.drawer.vehicleArrivalSealPhotos');
    const remarkLabel = t('common.remark');

    const { compareTimeColor } = useTaskCommon();
    const { renderTimeRow, renderPhotoRow, renderRemarkRow } = useStepDetailRender({ isEditing });
    const actualArrivalColor = compareTimeColor(waypoint.planArrivalAt, waypoint.actualArrivalAt);

    /**
     * 开始编辑
     */
    useEffect(() => {
        setPlannedArrivalTime(formatTimeDisplay(waypoint.planArrivalAt));
        setActualArrivalTime(formatTimeDisplay(waypoint.actualArrivalAt));
        setActualDraft(waypoint.actualArrivalAt ? dayjs(waypoint.actualArrivalAt) : null);
    }, [waypoint.planArrivalAt, waypoint.actualArrivalAt]);

    /**
     * 设置车辆到达照片
     */
    useEffect(() => {
        setArrivalPhotoUrls(waypoint.arrivalPhotosUrlsJson || []);
    }, [waypoint.arrivalPhotosUrlsJson]);

    useEffect(() => {
        setArrivalSealPhotoUrls(waypoint.arrivalSealPhotosUrlsJson || []);
    }, [waypoint.arrivalSealPhotosUrlsJson]);

    useEffect(() => {
        if (!isEditing) {
            setPhotosDraft(waypoint.arrivalPhotosJson || []);
            setRemarkDraft(waypoint.arrivedRemark || '');
        }
    }, [waypoint.arrivalPhotosJson, waypoint.arrivedRemark, isEditing]);

    useEffect(() => {
        if (!isEditing && showSealPhotos) {
            setSealPhotosDraft(waypoint.arrivalSealPhotosJson || []);
        }
    }, [waypoint.arrivalSealPhotosJson, isEditing, showSealPhotos]);

    /**
     * 必填项验证
     */
    const validateSave = () => {
        if (!actualDraft) {
            message.error(t('tms.task.drawer.actualArrivalTimeRequired'));
            return;
        }

        if (photosDraft.length === 0) {
            message.error(t('tms.task.drawer.vehicleArrivalPhotosRequired'));
            return;
        }

        // 途经点、终到站必须上传车辆封签照片；始发站不强制
        if (showSealPhotos && sealPhotosDraft.length === 0) {
            message.error(t('tms.task.drawer.vehicleArrivalSealPhotosRequired'));
            return;
        }

        // 交接任务 PC 到达：司机1/司机1电话/车牌号/柜号 一次性校验，缺失字段名合并成一条提示
        // （校验的是 driver1_*，字段名也点名司机1，避免与司机2 混淆）
        if (task?.routeType === TmsRouteTypeEnum.Handover) {
            const missing: string[] = [];
            if (!task.driver1Name?.trim()) missing.push(String(t('tms.task.drawer.driver1')));
            if (!task.driver1PhoneNumber?.trim()) missing.push(String(t('tms.task.drawer.driver1Phone')));
            if (!task.licensePlate?.trim()) missing.push(String(t('tms.task.drawer.licensePlate')));
            if (!task.trailerNumber?.trim()) missing.push(String(t('tms.task.drawer.trailerNumber')));
            if (missing.length > 0) {
                message.error(String(t('tms.task.drawer.arriveRequiredFields', { fields: missing.join(', ') })));
                return;
            }
        }

        return true;
    };


    /**
     * 保存司机到达详情
     */
    const handleSave = async () => {

        if (!validateSave()) {
            return;
        }

        const formattedActual = actualDraft ? actualDraft.format('YYYY-MM-DD HH:mm') : null;
        const accountId = getUserInfo()?.account?.id ? Number(getUserInfo()!.account.id) : undefined;

        const variables = {
            input: {
                taskId: waypoint.taskId,
                tmsTaskWayPointId: Number(waypoint.id),
                photosS3KeysJson: photosDraft,
                ...(showSealPhotos && { arrivalSealPhotosJson: sealPhotosDraft }),
                accountId,
                actualArrivalLocalTime: formattedActual,
                arrivedRemark: remarkDraft,
            },
        };

        try {
            const { data } = await updateArrivedMutation({
                variables,
                refetchQueries: [{ query: GET_TMS_TASK_DETAIL, variables: { taskId: String(waypoint.taskId) } }],
            });
            if (!data?.updateTmsTaskWaypointToArrivedForPortal) {
                throw new Error(t('tms.task.drawer.saveFailed'));
            }

            setActualArrivalTime(formatTimeDisplay(variables.input.actualArrivalLocalTime || actualArrivalTime));
            message.success(t('tms.task.drawer.saveSuccess'));
            exitEdit();
        } catch (error: any) {
            console.error('Failed to save driver arrival detail:', error);
            message.error(translateBackendError(error));
        }
    };

    return (
        <div className="relative border-gray-200">
            <EditToolbar
                isEditing={isEditing}
                allowEdit={stepDetailAllowEdit}
                onStartEdit={guardedStartEdit}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <div className="mt-1 space-y-3">
                {/* 计划到达时间 */}
                {renderTimeRow(plannedArrivalTimeLabel, plannedArrivalTime, plannedArrivalTimezone, null, () => {}, false)}
                {/* 静态预计到达时间（Static ETA，只读，由发车后级联计算下发）；始发站无预计到达，不展示 */}
                {waypoint.waypointType !== WaypointTypeEnum.Origin &&
                    renderTimeRow(staticEtaLabel, formatTimeDisplay(waypoint.staticEtaAt), waypoint.staticEtaTimezone, null, () => {}, false)}
                {/* 实际到达时间 */}
                {renderTimeRow(actualArrivalTimeLabel, actualArrivalTime, actualArrivalTimezone, actualDraft, setActualDraft, true, waypoint.actualArrivalTimezone, actualArrivalColor)}
                {/* 车辆到达照片 */}
                {renderPhotoRow(photoLabel, arrivalPhotoUrls, photosDraft, setPhotosDraft, arrivalPhotoUrls)}
                {/* 车辆封签照片：仅 waypointType=2(途经点)、3(终点) 展示 */}
                {showSealPhotos && renderPhotoRow(sealPhotoLabel, arrivalSealPhotoUrls, sealPhotosDraft, setSealPhotosDraft, arrivalSealPhotoUrls)}
                {renderRemarkRow(remarkLabel, waypoint.arrivedRemark, remarkDraft, setRemarkDraft)}
            </div>
        </div>
    );
};

export default DriverArrivedDetail;
